import { Children, type HTMLAttributes, type ReactElement, type Ref } from "react";
import { Autocomplete, IconButton, InputAdornment, Stack, TextField, type SxProps, type Theme } from "@mui/material";
import { InlineProgress } from "@gui/widgets/InlineProgress";
import { Clear, Search } from "@mui/icons-material";
import { AsyncStatus } from "@gui/widgets/A11y";
import { BusyButton } from "@gui/widgets/BusyButton";
import { JobLookupPane } from "./JobLookupDetails";
import { JobLookupDropdownPaper, PANE_DROPDOWN_WIDTH } from "./JobLookupDropdownPaper";
import { useJobLookupViewModel, type JobLookupProps } from "./JobLookupField.vm";
import type { JobLookupRow } from "./JobLookupSearch.vm";
import { JobLookupOption } from "./JobLookupOption";
import { JobLookupSyntaxHint } from "./JobLookupSyntaxHint";

/** How tall the box stands: MUI's small outlined field. The button beside it takes the same, and
 *  so should anything else that shares a header row with it — a row of controls at three
 *  heights reads as three rows. */
export const FIELD_HEIGHT = 40;

interface JobLookupFieldProps extends JobLookupProps {
	/** What the button says; "Load" unless told otherwise. */
	submitLabel?: string;
	/** Drop the button: Enter alone looks the job up, and a spinner in the box shows it working. */
	hideSubmit?: boolean;
	/** Contained when the lookup is the page's main act, outlined when it is a side door. */
	variant?: "contained" | "outlined";
	/** The box takes the row's width instead of a fixed one. */
	fullWidth?: boolean;
	/** Button under the box rather than beside it, for a phone. */
	stacked?: boolean;
	disabled?: boolean;
	autoFocus?: boolean;
	/** Shorter wording for a narrow box. */
	placeholder?: string;
	/** The 28px height of a list-header control, for a box in a panel's header. */
	dense?: boolean;
	sx?: SxProps<Theme>;
	inputRef?: Ref<HTMLInputElement>;
	/** The keys that focus the box, for a host that binds one. */
	"aria-keyshortcuts"?: string;
}

// The server ranks the rows, so the list shows them as they came.
const asRanked = (options: JobLookupRow[]) => options;
// Never drawn, and not the title: see `rowLabel`. MUI keys the rows by it too.
const optionLabel = (option: JobLookupRow | string) => (typeof option === "string" ? option : option.rowLabel);

/** MUI's own resync of `autoHighlight` runs on a change of row count or text, so a same-length set
 *  of new rows under unchanged text would draw no highlight and leave Enter on the old index. Keyed
 *  by its rows, whose keys are unique to the response, every response mounts a new list and MUI
 *  resyncs when its ref lands on it, even for the same jobs. That is what lets the details follow
 *  the highlight: MUI reports no move of its own, and after any response it is on the top row. A set of
 *  another length gets the resync either way, and `rowLabel` keeps it from finding the old row.
 *  MUI's styled list passes its class through to this `ul`. */
function ResultsListbox({
	children,
	ref,
	...props
}: HTMLAttributes<HTMLUListElement> & { ref?: Ref<HTMLUListElement> }) {
	const resultSet = Children.toArray(children)
		.map((row) => (row as ReactElement).key)
		.join();
	return (
		<ul key={resultSet} ref={ref} {...props}>
			{children}
		</ul>
	);
}

/** The dropdown stands wider than a narrow box, and clear of both window edges by this much. */
const VIEWPORT_MARGIN = 16;

// Slides the dropdown back from the edge a wide list would otherwise run past.
const DROPDOWN_MODIFIERS = [{ name: "preventOverflow", options: { padding: VIEWPORT_MARGIN } }];

/** How much of the window the AppBar's results may take, over MUI's default of 40%. */
const PANE_LIST_MAX_HEIGHT = "75dvh";

const dropdownSlotProps = (width: number, listMaxHeight?: string) =>
	({
		popper: {
			placement: "bottom-start",
			modifiers: DROPDOWN_MODIFIERS,
			// `&&&` to outrank App.css's 250px floor on every popper, which a plain `minWidth` loses to.
			sx: { "&&&": { minWidth: `min(${width}px, calc(100vw - ${2 * VIEWPORT_MARGIN}px))` } },
		},
		listbox: {
			"aria-label": "Job search results",
			...(listMaxHeight && { sx: { maxHeight: listMaxHeight } }),
		},
	}) as const;

const LIST_DROPDOWN = dropdownSlotProps(420);
const PANE_DROPDOWN = dropdownSlotProps(PANE_DROPDOWN_WIDTH, PANE_LIST_MAX_HEIGHT);
const DROPDOWN_SLOTS = { paper: JobLookupDropdownPaper };

/** One box for finding a job by whatever is to hand — words from its field, grower or company, its
 *  record id, its Airtable link, or its exact name — shared by the pages that take a job the
 *  operator names rather than one a list offers. Typing offers rows to an admin; picking one, by
 *  Enter or click, takes that job. The button or Enter opens a record id or link, finds a job's
 *  whole name (it holds `__`), and on anything else takes the top row once the rows answer the box.
 *  The X clears; a miss is said under the box. */
export const JobLookupField: React.FC<JobLookupFieldProps> = ({
	submitLabel = "Load",
	hideSubmit = false,
	variant = "contained",
	fullWidth = false,
	stacked = false,
	disabled = false,
	autoFocus = false,
	placeholder = "Field, grower, company, record ID or link",
	dense = false,
	sx,
	inputRef,
	"aria-keyshortcuts": keyShortcuts,
	...lookup
}) => {
	const vm = useJobLookupViewModel(lookup);
	const { paneRow } = vm;
	return (
		<>
			<AsyncStatus
				state={vm.loading ? "loading" : vm.noticeState}
				loadingText="Looking up the job…"
				emptyText={vm.notice ?? undefined}
				errorText={vm.notice ?? undefined}
			/>
			<Stack
				direction={stacked ? "column" : "row"}
				spacing={1}
				alignItems={stacked ? "stretch" : "flex-start"}
				component="form"
				onSubmit={vm.submit}
				sx={sx}
			>
				<Autocomplete<JobLookupRow, false, false, true>
					freeSolo
					size="small"
					// Never holds a value: a pick acts and is gone, and the box's text is `inputValue`.
					value={null}
					// `true` is the behaviour, no clear button of MUI's; the cast keeps `value={null}`, which
					// the type rejects when this is true.
					disableClearable={true as false}
					forcePopupIcon={false}
					fullWidth={fullWidth}
					disabled={disabled || vm.loading}
					inputValue={vm.value}
					onInputChange={vm.onInputChange}
					onChange={vm.onOptionChosen}
					onKeyDown={vm.onKeyDown}
					options={vm.options}
					filterOptions={asRanked}
					getOptionLabel={optionLabel}
					renderOption={({ key, ...props }, option) => <JobLookupOption key={key} result={option} {...props} />}
					autoHighlight
					onHighlightChange={vm.onHighlightChange}
					open={vm.dropdownOpen && !disabled}
					onOpen={vm.onOpen}
					onClose={vm.onClose}
					// The dropdown's one way to say something with no rows: freeSolo hides noOptionsText.
					loading={vm.notice !== null}
					loadingText={vm.notice}
					ListboxComponent={ResultsListbox}
					slots={DROPDOWN_SLOTS}
					slotProps={{
						...(lookup.showDetails ? PANE_DROPDOWN : LIST_DROPDOWN),
						paper: {
							pane: paneRow && <JobLookupPane row={paneRow} />,
							onPaneMouseDown: vm.onPaneMouseDown,
							// With the details, because the AppBar's box is the one with room for it.
							hint: lookup.showDetails && <JobLookupSyntaxHint />,
						},
					}}
					sx={[
						fullWidth || stacked ? { flex: 1, minWidth: 0 } : { width: 420 },
						// Autocomplete's vertical padding would stand the box 2px taller once the X shows.
						{
							"& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
								height: dense ? 28 : FIELD_HEIGHT,
								py: 0,
								flexWrap: "nowrap",
							},
						},
						dense && { "& .MuiInputBase-root": { typography: "dense" } },
					]}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder={placeholder}
							error={vm.error !== null}
							helperText={lookup.onErrorChange ? undefined : vm.error}
							autoFocus={autoFocus}
							inputRef={inputRef}
							onFocus={vm.onFocus}
							onBlur={vm.onBlur}
							inputProps={{
								...params.inputProps,
								"aria-label": "Search jobs by field, grower, company, record ID or link",
								"aria-keyshortcuts": keyShortcuts,
								spellCheck: false,
								onPaste: vm.handlePaste,
							}}
							InputProps={{
								...params.InputProps,
								startAdornment: (
									<InputAdornment position="start">
										<Search fontSize="small" />
									</InputAdornment>
								),
								endAdornment:
									hideSubmit && vm.loading ? (
										<InputAdornment position="end">
											<InlineProgress color="inherit" />
										</InputAdornment>
									) : vm.value ? (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={vm.clear}
												aria-label="Clear"
												disabled={disabled || vm.loading}
												sx={dense ? { p: 0.25 } : undefined}
											>
												<Clear fontSize="small" />
											</IconButton>
										</InputAdornment>
									) : undefined,
							}}
						/>
					)}
				/>
				{/* Without the button, the form still submits on Enter: one text field is enough for that. */}
				{!hideSubmit && (
					<BusyButton
						type="submit"
						variant={variant}
						busy={vm.loading}
						busyLabel="Looking up the job"
						disabled={disabled || !vm.canSubmit}
						sx={{ whiteSpace: "nowrap", height: FIELD_HEIGHT, flexShrink: 0 }}
					>
						{submitLabel}
					</BusyButton>
				)}
			</Stack>
		</>
	);
};
